import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import type {
  StoreAdminStatsResponse,
  StoreSubscriptionAssignmentView,
  StoreSubscriptionPaymentView,
  SubscriptionPlanView,
} from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import {
  getBuyerAbsoluteUrl,
  getSellerAbsoluteUrl,
} from "@/lib/routing/portal-urls";
import { PageActionButton } from "@/shared/layout/PageHeader";
import { ShareLinkButton } from "@/shared/components/ShareLinkButton";
import { StoreStatsCard } from "@/modules/stores/components/StoreStatsCard";
import { StoreSubscriptionsTab } from "@/modules/stores/components/subscriptions/StoreSubscriptionsTab";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useStoreByIdQuery } from "@/modules/stores/services/queries/useStoreByIdQuery";
import { useStoreStatsQuery } from "@/modules/stores/services/queries/useStoreStatsQuery";
import { useStoreSubscriptionAssignmentsQuery } from "@/modules/stores/services/queries/useStoreSubscriptionAssignmentsQuery";
import { useStoreSubscriptionPaymentsQuery } from "@/modules/stores/services/queries/useStoreSubscriptionPaymentsQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { LoadingSkeleton, ErrorState } from "@a2z/ui";

type StoreDetailPageProps = {
  locale: Locale;
  storeId: string;
  showArchived: boolean;
};

const defaultStats: StoreAdminStatsResponse = {
  orders: { total: 0, pending: 0, delivered: 0 },
  catalog: { products: 0, categories: 0 },
};

export function StoreDetailPage({
  locale,
  storeId,
  showArchived,
}: StoreDetailPageProps) {
  const navigate = useNavigate();
  const t = useTranslations();

  const { data: session } = useSessionQuery();
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useStoreByIdQuery({ storeId });
  const { data: statsData, isLoading: isStatsLoading, error: statsError } =
    useStoreStatsQuery({ storeId });
  const {
    data: assignmentsData,
    isLoading: isAssignmentsLoading,
    error: assignmentsError,
  } = useStoreSubscriptionAssignmentsQuery({ storeId });
  const { data: paymentsData, isLoading: isPaymentsLoading, error: paymentsError } =
    useStoreSubscriptionPaymentsQuery({
      storeId,
      includeDeleted: showArchived,
    });
  const { data: plansData, isLoading: isPlansLoading, error: plansError } =
    useSubscriptionsQuery();
  const storeErrorStatus = getQueryErrorStatus(error);

  useEffect(() => {
    if (storeErrorStatus === 404) {
      navigate(`/${locale}/admin/stores`, { replace: true });
    }
  }, [locale, navigate, storeErrorStatus]);

  if (isLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (storeErrorStatus === 404) {
    return null;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const storeSlug = data.store.storeSlug;
  const sellerDashboardPath = getSellerAbsoluteUrl(locale, storeSlug);
  const storefrontPath = getBuyerAbsoluteUrl(locale, storeSlug);

  const isPaymentsToggleVisible =
    session?.admin?.roles?.some((role) =>
      [ADMIN_ROLES.ADMIN as string, ADMIN_ROLES.SUPER_ADMIN as string].includes(role),
    ) ?? false;

  const isSubDataLoading =
    isStatsLoading || isAssignmentsLoading || isPaymentsLoading || isPlansLoading;

  const stats = statsError ? defaultStats : statsData ?? defaultStats;
  const assignments: StoreSubscriptionAssignmentView[] = assignmentsError
    ? []
    : assignmentsData ?? [];
  const payments: StoreSubscriptionPaymentView[] = paymentsError ? [] : paymentsData ?? [];
  const plans: SubscriptionPlanView[] = plansError ? [] : plansData?.items ?? [];

  return (
    <section className="flex w-full max-w-4xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[23px] font-bold leading-tight tracking-[-0.025em] lg:text-[28px] lg:font-semibold lg:tracking-[-0.01em] text-[color:var(--text-primary)]">
            {data.store.storeName}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ShareLinkButton
            path={sellerDashboardPath}
            label={t(keys.admin.shareSellerDashboardTooltip)}
            copiedLabel={t(keys.admin.linkCopiedTooltip)}
            ariaLabel={t(keys.admin.shareSellerDashboardTooltip)}
            size="sm"
          />
          <ShareLinkButton
            path={storefrontPath}
            label={t(keys.admin.shareStorefrontTooltip)}
            copiedLabel={t(keys.admin.linkCopiedTooltip)}
            ariaLabel={t(keys.admin.shareStorefrontTooltip)}
            size="sm"
          />
          <PageActionButton
            href={`/${locale}/admin/sellers/${data.seller.id}`}
            label={t(keys.admin.viewSeller)}
          />
        </div>
      </div>

      {isSubDataLoading ? (
        <LoadingSkeleton variant="card" rows={3} />
      ) : (
        <>
          <StoreStatsCard stats={stats} t={t} />

          <StoreSubscriptionsTab
            locale={locale}
            storeId={data.store.id}
            assignments={assignments}
            payments={payments}
            plans={plans}
            showArchived={showArchived}
            isPaymentsToggleVisible={isPaymentsToggleVisible}
            t={t}
          />
        </>
      )}
    </section>
  );
}
